/**
 * Token-bound monograph typography primitives.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { cva } from "class-variance-authority";
import { motion, useReducedMotion } from "motion/react";
import { cn } from "../styling/index.js";
import type {
  MonographPanelTokens,
  MonographProseProps,
  MonographProseTone,
  MonographProseVariant,
} from "./MonographPanelPrimitiveTypes.js";

const proseClass = cva("csk-monograph-prose", {
  variants: {
    variant: {
      body: "csk-monograph-prose--body",
      caption: "csk-monograph-prose--caption",
      lead: "csk-monograph-prose--lead",
      note: "csk-monograph-prose--note",
    },
  },
  defaultVariants: { variant: "body" },
});

const proseRhythm: Readonly<
  Record<MonographProseVariant, Pick<React.CSSProperties, "fontSize" | "fontStyle" | "lineHeight">>
> = {
  body: { fontSize: 13.5, lineHeight: 1.7 },
  caption: { fontSize: 12.5, lineHeight: 1.65 },
  lead: { fontSize: 14, lineHeight: 1.75 },
  note: { fontSize: 12.5, fontStyle: "italic", lineHeight: 1.65 },
};

interface TypographyFactoryInput {
  readonly tokens: MonographPanelTokens;
}

type MotionParagraphStyle = NonNullable<React.ComponentProps<typeof motion.p>["style"]>;

function toneColor(tokens: MonographPanelTokens, tone: MonographProseTone): string {
  if (tone === "bright") return tokens.bright;
  if (tone === "muted") return tokens.muted;
  return tokens.text;
}

export function createMonographPanelTypography({ tokens }: TypographyFactoryInput): {
  readonly Prose: (props: MonographProseProps) => React.JSX.Element;
} {
  function Prose({
    children,
    variant = "body",
    tone,
    color,
    className,
    style,
  }: MonographProseProps): React.JSX.Element {
    const reducedMotion = useReducedMotion() ?? false;
    const resolvedTone = tone ?? (variant === "caption" || variant === "note" ? "muted" : "text");
    const proseStyle = {
      color: color ?? toneColor(tokens, resolvedTone),
      margin: 0,
      ...proseRhythm[variant],
      ...style,
    } as MotionParagraphStyle;
    const motionProps = reducedMotion
      ? { initial: false as const }
      : {
          animate: { opacity: 1, y: 0 },
          initial: { opacity: 0, y: 3 },
          transition: { duration: 0.14 },
        };

    return (
      <motion.p
        className={cn(proseClass({ variant }), className)}
        {...motionProps}
        style={proseStyle}
      >
        {children}
      </motion.p>
    );
  }

  return { Prose };
}
